'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { ProgressStepperRootContext } from '../progress-stepper/context/progress-stepper-root.context';
import { ProgressRootContext } from './context/progress-root.context';
import { progressRangeRecipe } from './styles/progress-range.styles';
import type { ProgressRangePropsInterface } from './types/progress-range.types';

/**
 * The active highlighted range of the progress bar's track
 *
 * @related ['ProgressBar']
 * @since 10.0.0
 */
export const ProgressRange = forwardRef<HTMLDivElement, ProgressRangePropsInterface>(
  (props: ProgressRangePropsInterface, forwardedRef) => {
    const context = useContext(ProgressRootContext);
    const { orientation } = useContext(ProgressStepperRootContext);

    const {
      className,
      css: cssProp,
      size = context.size || 'sm',
      percentComplete = context.percentComplete || 0,
      ...rest
    } = props;

    const styles = css(
      progressRangeRecipe.raw({
        size,
        complete: percentComplete >= 100,
      }),
      cssProp,
    );

    // Switches to height when a parent ProgressStepper is set to vertical orientation
    const sizeProperty = useMemo(() => {
      return orientation === 'vertical' ? 'height' : 'width';
    }, [orientation]);

    return (
      <div
        className={cx('c11n', 'c11n_progress__range', styles, className)}
        data-c11n-component="Progress.Range"
        data-c11n-version={__C11N_VERSION__}
        ref={forwardedRef}
        style={{
          [sizeProperty]: `${percentComplete}%`,
        }}
        {...rest}
      />
    );
  },
);

ProgressRange.displayName = 'Progress.Range';
